/** * Main authenticated shell of the admin center. * * The layout owns the persistent navigation,
breadcrumbs, page header, loading * indicators, and global shell interactions such as sidebar
collapse state. * Feature pages are rendered inside the nested router outlet. */
<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { ref, computed, nextTick, onMounted, watch, onUnmounted } from 'vue'
import axiosInstance from '@/plugins/axios'
import { useSessionStore } from '@/store/sessionStore'
import { getMenuItems } from '@/composables/menuItems'
import { useAuth } from '@/composables/useAuth'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import FooterBar from '@/components/general/FooterBar.vue'
import Breadcrumb from 'primevue/breadcrumb'
import { appendAuthPayload, getStoredRedirectSessionPayload } from '@/router/redirect'
import HelpDrawer from '@/components/general/HelpDrawer.vue'
import { providePageHeader } from '@/composables/usePageHeader'
import { providePreviewPanel } from '@/composables/usePreviewPanel'
import { useI18n } from 'vue-i18n'

const pageHeader = providePageHeader()
const previewPanel = providePreviewPanel()
const { t } = useI18n()

const { signOut } = useAuth()
const route = useRoute()
const router = useRouter()
const sessionStore = useSessionStore()
const userFirstName = ref('')
const userEmail = ref('')
const isLoading = ref(false)
const blockUi = ref(false)
const isNavbarCollapsed = ref(localStorage.getItem('sidebar-collapsed') === 'true')
const activeSubmenu = ref<string | null>(null)
const unsavedDialogVisible = ref(false)
const unsavedDialogBusy = ref(false)
let unsavedDialogResolve: ((value: boolean) => void) | null = null

const resolveMeta = (value: unknown) =>
  typeof value === 'function' ? (value as () => string)() : value

const effectiveTitle = computed(
  () => pageHeader.state.title ?? String(resolveMeta(route.meta.title) ?? '')
)

const breadcrumbItems = computed(() =>
  route.matched
    .filter((r) => r.meta?.breadcrumb || r.meta?.title)
    .map((r) => ({
      label: String(resolveMeta(r.meta.breadcrumb) ?? resolveMeta(r.meta.title) ?? ''),
      route: r.path
    }))
)

const breadcrumbHome = { icon: 'pi pi-home', route: '/' }

const menuItems = computed<any[]>(() => getMenuItems(sessionStore.tenant) as any[])

const allowedItems = computed<any[]>(() =>
  menuItems.value.filter(
    (item: any) =>
      item.allowedRoles?.includes(sessionStore.user?.role) || item.allowedRoles?.includes('all')
  )
)

const filteredMenuItems = computed<any[]>(() =>
  allowedItems.value.filter((item: any) => !item.bottom)
)
const bottomMenuItems = computed<any[]>(() => allowedItems.value.filter((item: any) => item.bottom))

const userInitials = computed(() => {
  const name = userFirstName.value || sessionStore.user?.firstName || ''
  return name ? name.charAt(0).toUpperCase() : '?'
})

const userDisplayName = computed(() => userFirstName.value || sessionStore.user?.firstName || '')
const userDisplayEmail = computed(() => userEmail.value || sessionStore.user?.email || '')
const profileRoute = '/personalSettings'

const matchesRoute = (path?: string) => {
  if (!path || path === '/') return route.path === path
  return route.path === path || route.path.startsWith(`${path}/`)
}

const currentSubmenuLabel = computed<string | null>(() => {
  const activeParent = filteredMenuItems.value.find(
    (item: any) =>
      item.items?.length &&
      (matchesRoute(item.to) || item.items.some((subItem: any) => matchesRoute(subItem.to)))
  )

  return activeParent?.label ?? null
})

const fetchUserData = async () => {
  try {
    const { data } = await axiosInstance.get('/auth/user', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
    userFirstName.value = data.firstName
    userEmail.value = data.email ?? ''
  } catch (error) {
    console.error('Failed to fetch user data', error)
  }
}

const toggleNavbar = () => {
  isNavbarCollapsed.value = !isNavbarCollapsed.value
  localStorage.setItem('sidebar-collapsed', isNavbarCollapsed.value.toString())
}

const goBack = () => {
  if (window.history.length > 1 && route.path !== '/') {
    router.back()
  }
}

const requestUnsavedNavigationDecision = () => {
  if (!pageHeader.state.dirty) return Promise.resolve(true)

  unsavedDialogVisible.value = true

  return new Promise<boolean>((resolve) => {
    unsavedDialogResolve = resolve
  })
}

const resolveUnsavedDialog = (value: boolean) => {
  unsavedDialogVisible.value = false
  unsavedDialogBusy.value = false
  unsavedDialogResolve?.(value)
  unsavedDialogResolve = null
}

const stayOnCurrentPage = () => {
  resolveUnsavedDialog(false)
}

const discardUnsavedChanges = async () => {
  unsavedDialogBusy.value = true

  try {
    if (pageHeader.state.reset) {
      await pageHeader.state.reset.handler()
      await nextTick()
    }

    pageHeader.setDirty(false)
    resolveUnsavedDialog(true)
  } catch (error) {
    console.error('Failed to discard unsaved changes:', error)
    unsavedDialogBusy.value = false
  }
}

const saveUnsavedChanges = async () => {
  if (!pageHeader.state.save) {
    return discardUnsavedChanges()
  }

  try {
    unsavedDialogBusy.value = true
    await pageHeader.state.save.handler()
    await nextTick()

    if (pageHeader.state.dirty) {
      unsavedDialogBusy.value = false
      return
    }

    resolveUnsavedDialog(true)
  } catch (error) {
    console.error('Failed to save unsaved changes before navigation:', error)
    unsavedDialogBusy.value = false
  }
}

onBeforeRouteUpdate(() => requestUnsavedNavigationDecision())
onBeforeRouteLeave(() => requestUnsavedNavigationDecision())

const handleSubmenuHover = (item: any) => {
  activeSubmenu.value = item.items ? item.label : currentSubmenuLabel.value
}

const handleSubmenuLeave = () => {
  activeSubmenu.value = currentSubmenuLabel.value
}

watch(route, () => {
  activeSubmenu.value = currentSubmenuLabel.value
})

onMounted(() => {
  fetchUserData()
  checkForActiveBug()
})

window.addEventListener('start-loading', () => (isLoading.value = true))
window.addEventListener('stop-loading', () => (isLoading.value = false))
window.addEventListener('block-ui', () => (blockUi.value = true))
window.addEventListener('unblock-ui', () => (blockUi.value = false))

// Keep the dashboard link in sync because the home route resolves to an empty child path.
const checkForActiveBug = () => {
  const navLinks = document.querySelectorAll('.sidebar-menu-item')
  if (!navLinks.length) return

  if (route.path === '/') {
    navLinks[0].classList.add('router-link-active')
  } else {
    navLinks[0].classList.remove('router-link-active')
  }
}
watch(route, () => {
  checkForActiveBug()
})

const handleResize = () => {
  if (window.innerWidth < 768) {
    isNavbarCollapsed.value = true
    localStorage.setItem('sidebar-collapsed', 'true')
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  handleResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (!pageHeader.state.dirty) return
  event.preventDefault()
  event.returnValue = ''
}

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

const openExternalWithAuth = (href: string) => {
  const authPayload = getStoredRedirectSessionPayload()
  const url = appendAuthPayload(new URL(href), authPayload)
  window.open(url, '_blank')
}

const isSubmenuOpen = (item: any) =>
  !isNavbarCollapsed.value &&
  !!item.items?.length &&
  (activeSubmenu.value === item.label || currentSubmenuLabel.value === item.label)
</script>

<template>
  <BlockUI :blocked="blockUi">
    <div class="app-shell">
      <aside class="app-sidebar" :class="{ collapsed: isNavbarCollapsed }">
        <div class="sidebar-brand">
          <img
            src="/img/TUEV-AILAB-Logo_schwarz.png"
            class="sidebar-brand-logo"
            :class="{ 'sidebar-brand-logo--collapsed': isNavbarCollapsed }"
          />
        </div>

        <nav class="sidebar-menu" @mouseleave="handleSubmenuLeave">
          <template v-for="item in filteredMenuItems" :key="item.label">
            <div class="sidebar-menu-group" @mouseenter="handleSubmenuHover(item)">
              <a
                v-if="item.external"
                class="sidebar-menu-item"
                href="#"
                @click.prevent="openExternalWithAuth(item.href!)"
              >
                <FontAwesomeIcon :icon="item.icon" class="sidebar-menu-icon" />
                <span v-if="!isNavbarCollapsed" class="sidebar-menu-label">{{ item.label }}</span>
              </a>
              <router-link v-else :to="item.to" class="sidebar-menu-item">
                <FontAwesomeIcon :icon="item.icon" class="sidebar-menu-icon" />
                <span v-if="!isNavbarCollapsed" class="sidebar-menu-label">{{ item.label }}</span>
              </router-link>

              <div v-if="isSubmenuOpen(item)" class="sidebar-submenu">
                <router-link
                  v-for="subItem in item.items"
                  :key="subItem.label"
                  :to="subItem.to"
                  class="sidebar-submenu-item"
                >
                  <FontAwesomeIcon :icon="subItem.icon" class="sidebar-menu-icon" />
                  <span class="sidebar-menu-label">{{ subItem.label }}</span>
                </router-link>
              </div>
            </div>
          </template>
        </nav>

        <div class="sidebar-bottom">
          <template v-for="item in bottomMenuItems" :key="item.label">
            <a
              v-if="item.external"
              class="sidebar-menu-item"
              href="#"
              @click.prevent="openExternalWithAuth(item.href!)"
            >
              <FontAwesomeIcon :icon="item.icon" class="sidebar-menu-icon" />
              <span v-if="!isNavbarCollapsed" class="sidebar-menu-label">{{ item.label }}</span>
            </a>
            <router-link v-else :to="item.to" class="sidebar-menu-item">
              <FontAwesomeIcon :icon="item.icon" class="sidebar-menu-icon" />
              <span v-if="!isNavbarCollapsed" class="sidebar-menu-label">{{ item.label }}</span>
            </router-link>
          </template>
          <button
            type="button"
            class="sidebar-menu-item sidebar-menu-item--action"
            @click="signOut"
          >
            <FontAwesomeIcon icon="fa-solid fa-sign-out" class="sidebar-menu-icon" />
            <span v-if="!isNavbarCollapsed" class="sidebar-menu-label">Sign Out</span>
          </button>
        </div>

        <router-link
          :to="profileRoute"
          class="sidebar-user"
          :class="{ 'sidebar-user--collapsed': isNavbarCollapsed }"
        >
          <div class="sidebar-user-avatar">
            <template v-if="userInitials && userInitials !== '?'">{{ userInitials }}</template>
            <FontAwesomeIcon v-else icon="fa-solid fa-user" />
          </div>
          <div v-if="!isNavbarCollapsed" class="sidebar-user-info">
            <div class="sidebar-user-name">{{ userDisplayName || 'User' }}</div>
            <div class="sidebar-user-email">{{ userDisplayEmail }}</div>
          </div>
        </router-link>
      </aside>

      <!-- ── Preview panel – owned by the shell, filled via Teleport ──── -->
      <aside
        v-show="previewPanel.state.active"
        id="app-preview-panel"
        class="app-preview-panel"
        :style="{ width: previewPanel.state.width, minWidth: previewPanel.state.width }"
      />

      <main class="app-main">
        <Breadcrumb
          v-if="breadcrumbItems.length"
          :model="breadcrumbItems"
          :home="breadcrumbHome"
          class="app-breadcrumb"
        >
          <template #item="{ item }">
            <router-link v-if="item.route" :to="item.route" class="app-breadcrumb-link">
              <i v-if="item.icon" :class="item.icon" />
              <span v-if="item.label">{{ item.label }}</span>
            </router-link>
            <span v-else class="app-breadcrumb-link">{{ item.label }}</span>
          </template>
        </Breadcrumb>
        <article class="app-content-card">
          <header class="app-content-header">
            <div class="app-content-header-left">
              <button
                type="button"
                class="app-toggle-btn"
                aria-label="Toggle sidebar"
                @click="toggleNavbar"
              >
                <FontAwesomeIcon icon="fa-solid fa-bars" />
              </button>

              <div class="app-content-heading">
                <h1 class="app-content-title">{{ effectiveTitle }}</h1>
                <p v-if="route.meta.description" class="app-content-description">
                  {{ resolveMeta(route.meta.description) }}
                </p>
              </div>
            </div>

            <div class="app-content-header-right">
              <template
                v-for="(action, index) in pageHeader.state.actions"
                :key="action.key || index"
              >
                <Select
                  v-if="action.kind === 'select'"
                  :modelValue="action.value"
                  :options="action.options"
                  :optionLabel="action.optionLabel || 'label'"
                  :optionValue="action.optionValue || 'value'"
                  :placeholder="action.placeholder"
                  :disabled="action.disabled"
                  size="small"
                  class="app-content-header-select"
                  @update:modelValue="action.onChange($event)"
                />
                <IconField v-else-if="action.kind === 'input'" class="app-content-header-input">
                  <InputIcon v-if="action.icon">
                    <FontAwesomeIcon :icon="action.icon" />
                  </InputIcon>
                  <InputText
                    :modelValue="action.value"
                    :placeholder="action.placeholder"
                    :disabled="action.disabled"
                    size="small"
                    class="app-content-header-input-control"
                    @update:modelValue="action.onChange(String($event ?? ''))"
                  />
                </IconField>
                <Button
                  v-else
                  :severity="action.severity || 'secondary'"
                  :outlined="action.outlined ?? true"
                  size="small"
                  :label="action.label"
                  :disabled="action.disabled"
                  :loading="action.loading"
                  @click="action.handler($event)"
                >
                  <template v-if="action.icon" #icon>
                    <FontAwesomeIcon :icon="action.icon" />&nbsp;
                  </template>
                </Button>
              </template>

              <Button
                v-if="route.path !== '/'"
                severity="secondary"
                outlined
                size="small"
                label="Back"
                :disabled="pageHeader.state.dirty"
                @click="goBack"
              >
                <template #icon> <FontAwesomeIcon :icon="['fas', 'arrow-left']" />&nbsp; </template>
              </Button>

              <Button
                v-if="pageHeader.state.reset"
                severity="secondary"
                outlined
                size="small"
                :label="pageHeader.state.reset.label || 'Zurücksetzen'"
                :disabled="pageHeader.state.reset.disabled ?? !pageHeader.state.dirty"
                @click="pageHeader.state.reset.handler"
              >
                <template #icon>
                  <FontAwesomeIcon
                    :icon="pageHeader.state.reset.icon || ['fas', 'rotate-left']"
                  />&nbsp;
                </template>
              </Button>

              <Button
                v-if="pageHeader.state.save"
                severity="primary"
                size="small"
                :label="pageHeader.state.save.label || 'Speichern'"
                :disabled="pageHeader.state.save.disabled ?? false"
                :loading="pageHeader.state.save.loading ?? false"
                @click="pageHeader.state.save.handler"
              >
                <template #icon>
                  <FontAwesomeIcon
                    :icon="pageHeader.state.save.icon || ['fas', 'floppy-disk']"
                  />&nbsp;
                </template>
              </Button>
            </div>
          </header>

          <nav v-if="pageHeader.state.tabs.length" class="app-content-tabs">
            <template v-for="tab in pageHeader.state.tabs" :key="tab.route ?? tab.value">
              <router-link
                v-if="tab.route"
                :to="tab.route"
                class="app-content-tab"
                active-class="active"
              >
                <FontAwesomeIcon v-if="tab.icon" :icon="tab.icon" class="app-content-tab-icon" />
                <span>{{ tab.label }}</span>
              </router-link>
              <button
                v-else
                type="button"
                class="app-content-tab"
                :class="{ active: tab.active }"
                @click="tab.value !== undefined && tab.onSelect?.(tab.value)"
              >
                <FontAwesomeIcon v-if="tab.icon" :icon="tab.icon" class="app-content-tab-icon" />
                <span>{{ tab.label }}</span>
              </button>
            </template>
          </nav>

          <ProgressBar v-if="isLoading" mode="indeterminate" class="app-content-progress" />

          <div class="app-content-body">
            <router-view />
          </div>
        </article>

        <FooterBar hidden class="app-footer" />
      </main>

      <HelpDrawer />

      <Dialog
        v-model:visible="unsavedDialogVisible"
        modal
        :closable="!unsavedDialogBusy"
        :header="t('general.unsavedChanges.title')"
        class="unsaved-dialog"
        @hide="!unsavedDialogBusy && stayOnCurrentPage()"
      >
        <p class="unsaved-dialog-copy">
          {{ t('general.unsavedChanges.message') }}
        </p>

        <template #footer>
          <Button
            :label="t('general.unsavedChanges.stayButton')"
            severity="secondary"
            text
            :disabled="unsavedDialogBusy"
            @click="stayOnCurrentPage"
          />
          <Button
            :label="t('general.unsavedChanges.discardButton')"
            severity="danger"
            outlined
            :disabled="unsavedDialogBusy"
            @click="discardUnsavedChanges"
          />
          <Button
            :label="t('general.unsavedChanges.saveButton')"
            :loading="unsavedDialogBusy"
            :disabled="pageHeader.state.save?.disabled ?? false"
            @click="saveUnsavedChanges"
          />
        </template>
      </Dialog>
    </div>
  </BlockUI>
</template>

<style scoped>
.app-shell {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  height: 100vh;
  overflow: hidden;
  background: #f7f7f5;
  box-sizing: border-box;
}

/* Sidebar */
.app-sidebar {
  order: 0;
  width: 240px;
  flex-shrink: 0;
  background: var(--tuev-light-blue, #00dce6);
  color: var(--tuev-dark-blue, #002864);
  border-radius: 18px;
  padding: 1rem 0.85rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: width 0.25s ease;
  box-shadow: 0 1px 2px rgba(15, 17, 21, 0.04);
  min-height: 0;
}

.app-sidebar.collapsed {
  width: 76px;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.25rem 0.5rem;
  border-bottom: 1px solid rgba(0, 40, 100, 0.12);
}

.sidebar-brand-logo {
  max-width: 100%;
  max-height: 44px;
  object-fit: contain;
}

.sidebar-brand-logo--collapsed {
  max-height: 32px;
}

.sidebar-menu {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  overflow-y: auto;
}

.sidebar-menu-item {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  text-decoration: none;
  color: var(--tuev-dark-blue, #002864);
  font-size: 0.9rem;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  text-align: left;
  width: 100%;
}

.sidebar-menu-item:hover {
  background: rgba(0, 40, 100, 0.08);
}

.sidebar-menu-item.router-link-active {
  background: var(--tuev-dark-blue, #002864);
  color: #ffffff;
}

.sidebar-menu-item.router-link-active .sidebar-menu-bullet {
  background: #ffffff;
  opacity: 1;
}

.sidebar-menu-bullet {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.55;
  flex-shrink: 0;
}

.sidebar-menu-icon {
  width: 16px;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.sidebar-menu-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Collapsed sidebar: center menu items so the active background looks balanced */
.app-sidebar.collapsed .sidebar-menu-item {
  justify-content: center;
  padding: 0.55rem 0;
  width: 44px;
  margin: 0 auto;
}

.app-sidebar.collapsed .sidebar-menu-icon {
  width: auto;
  font-size: 1rem;
}

.app-sidebar.collapsed .sidebar-menu-bullet {
  display: none;
}

/* ── Preview panel ─────────────────────────────────────────────────────────── */

.app-preview-panel {
  order: 2;
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(15, 17, 21, 0.04);
  overflow-y: auto;
  height: 100%;
  /* panel scrolls independently – main content is unaffected */
}

.sidebar-submenu {
  margin: 0.25rem 0 0 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-left: 0.5rem;
  border-left: 1px solid rgba(0, 40, 100, 0.15);
}

.sidebar-submenu-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  font-size: 0.85rem;
  text-decoration: none;
  color: var(--tuev-dark-blue, #002864);
}

.sidebar-submenu-item:hover,
.sidebar-submenu-item.router-link-active {
  background: rgba(0, 40, 100, 0.1);
}

.sidebar-bottom {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 40, 100, 0.12);
}

.sidebar-user {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.6rem;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 12px;
  border: 1px solid rgba(0, 40, 100, 0.06);
  text-decoration: none;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;
}

.sidebar-user:hover {
  background: rgba(255, 255, 255, 0.72);
  border-color: rgba(0, 40, 100, 0.14);
}

.sidebar-user.router-link-active {
  background: var(--tuev-dark-blue, #002864);
  border-color: var(--tuev-dark-blue, #002864);
}

.sidebar-user.router-link-active .sidebar-user-name,
.sidebar-user.router-link-active .sidebar-user-email {
  color: #ffffff;
}

.sidebar-user--collapsed {
  justify-content: center;
  padding: 0.4rem;
}

.sidebar-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--tuev-dark-blue, #002864);
  color: #ffffff;
  display: grid;
  place-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
}

.sidebar-user-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.sidebar-user-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--tuev-dark-blue, #002864);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-user-email {
  font-size: 0.72rem;
  color: rgba(0, 40, 100, 0.72);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Main area */
.app-main {
  order: 1;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.app-content-card {
  flex: 1;
  background: #ffffff;
  border: 1px solid #e8e6e0;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(15, 17, 21, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.app-content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.5rem;
  border-bottom: 1px solid #f0efea;
  flex-wrap: wrap;
}

.app-content-header-left {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
}

.app-toggle-btn {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: transparent;
  border: 1px solid #e8e6e0;
  color: #2e3137;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.app-toggle-btn:hover {
  background: #f7f7f5;
  border-color: #d7d4cc;
}

.app-content-heading {
  min-width: 0;
}

.app-breadcrumb {
  background: transparent !important;
  padding: 0.35rem 0.25rem !important;
  border: none !important;
  margin: 0;
  font-size: 0.78rem;
  align-self: flex-start;
}

.app-breadcrumb :deep(.p-breadcrumb-item-link),
.app-breadcrumb :deep(.p-breadcrumb-item-label),
.app-breadcrumb :deep(.p-breadcrumb-item),
.app-breadcrumb :deep(.p-breadcrumb-separator) {
  color: #9a9c9f !important;
}

.app-breadcrumb :deep(.p-breadcrumb-item:last-child .app-breadcrumb-link) {
  color: #2e3137 !important;
  font-weight: 500;
}

.app-breadcrumb-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #9a9c9f;
  text-decoration: none;
}

.app-breadcrumb-link:hover {
  color: var(--tuev-dark-blue, #002864);
}

.app-content-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1c1f;
  letter-spacing: -0.01em;
}

.app-content-description {
  margin: 0.15rem 0 0;
  font-size: 0.82rem;
  color: #6b6e75;
}

.app-content-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.app-content-header-select {
  min-width: 10rem;
}

.app-content-header-input {
  min-width: 16rem;
}

.app-content-header-input-control {
  width: 100%;
}

.app-content-tabs {
  display: flex;
  gap: 0;
  padding: 0 1.5rem;
  border-bottom: 1px solid #f0efea;
  background: #fafaf8;
  overflow-x: auto;
}

.app-content-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.1rem;
  color: #6b6e75;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.app-content-tab:hover {
  color: var(--tuev-dark-blue, #002864);
}

.app-content-tab.active {
  color: var(--tuev-dark-blue, #002864);
  border-bottom-color: var(--tuev-dark-blue, #002864);
  font-weight: 600;
}

.app-content-tab-icon {
  font-size: 0.8rem;
  opacity: 0.8;
}

.app-content-tab.active .app-content-tab-icon {
  opacity: 1;
}

.app-content-progress {
  height: 3px !important;
  border-radius: 0 !important;
}

.app-content-body {
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
}

.app-footer {
  padding: 0 0.25rem;
}

.unsaved-dialog {
  width: min(32rem, calc(100vw - 2rem));
}

.unsaved-dialog-copy {
  margin: 0;
  color: #4b5563;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
  .app-shell {
    padding: 0.5rem;
    gap: 0.5rem;
  }

  .app-sidebar {
    position: fixed;
    left: 0.5rem;
    top: 0.5rem;
    bottom: 0.5rem;
    z-index: 1000;
  }

  .app-sidebar.collapsed {
    transform: translateX(calc(-100% - 0.5rem));
    width: 240px;
  }

  .app-content-body {
    padding: 1rem;
  }
}
</style>
